import { createFileRoute } from "@tanstack/react-router";
import { createServerFn } from "@tanstack/react-start";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { z } from "zod";
import { Loader2, CheckCircle2, XCircle, AlertTriangle, ShieldCheck } from "lucide-react";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

type CheckResult = { name: string; status: "pass" | "warn" | "fail"; detail: string };
type Report = { ok: boolean; courseTitle?: string; checks: CheckResult[] };

const PLACEHOLDER_RE = /\b(lorem ipsum|tbd|todo|coming soon|placeholder|xxx{2,})\b/i;

async function assertAdmin(ctx: { supabase: any; userId: string }) {
  const { data, error } = await ctx.supabase.rpc("has_role", {
    _user_id: ctx.userId,
    _role: "admin",
  });
  if (error || !data) throw new Error("Forbidden");
}

export const qaVerifyCourse = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((i: unknown) => z.object({ slug: z.string().min(1).max(80) }).parse(i))
  .handler(async ({ data, context }): Promise<Report> => {
    await assertAdmin(context as any);
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const checks: CheckResult[] = [];

    const { data: course } = await supabaseAdmin
      .from("course_catalog")
      .select("slug, title, welcome_md, is_published")
      .eq("slug", data.slug)
      .maybeSingle();

    const c: any = course;
    if (!c) {
      return { ok: false, checks: [{ name: "Course exists", status: "fail", detail: `No course with slug "${data.slug}"` }] };
    }

    checks.push({
      name: "Start button routing",
      status: c.is_published ? "pass" : "warn",
      detail: c.is_published
        ? `/portal/academy/${c.slug} resolves and is published.`
        : "Course is not published; learners cannot enrol.",
    });

    const welcome = (c.welcome_md ?? "").trim();
    checks.push({
      name: "Welcome page content",
      status: welcome.length >= 120 ? "pass" : "warn",
      detail: welcome.length >= 120 ? `${welcome.length} characters of welcome content.` : "Welcome page is short or missing.",
    });
    if (welcome && PLACEHOLDER_RE.test(welcome)) {
      checks.push({ name: "Welcome placeholders", status: "fail", detail: "Welcome page contains placeholder text." });
    }

    const { data: modulesData } = await supabaseAdmin
      .from("course_modules")
      .select("id, title, lesson_md, body_md, notes_url, checklist_url, order_index")
      .eq("course_slug", data.slug)
      .order("order_index", { ascending: true });

    const mods: any[] = (modulesData as any[]) ?? [];
    checks.push({
      name: "Modules present",
      status: mods.length >= 5 ? "pass" : mods.length > 0 ? "warn" : "fail",
      detail: `${mods.length} module(s) configured.`,
    });

    let lessonsMissing = 0;
    let placeholderHits = 0;
    let downloadsOk = 0;
    let downloadsBroken = 0;
    for (const m of mods) {
      const txt = ((m.lesson_md ?? m.body_md ?? "") as string).trim();
      if (txt.length < 200) lessonsMissing++;
      if (PLACEHOLDER_RE.test(txt) || PLACEHOLDER_RE.test(m.title ?? "")) placeholderHits++;
      for (const url of [m.notes_url, m.checklist_url].filter(Boolean) as string[]) {
        try {
          const r = await fetch(url, { method: "HEAD" });
          if (r.ok) downloadsOk++;
          else downloadsBroken++;
        } catch {
          downloadsBroken++;
        }
      }
    }
    checks.push({
      name: "Lesson content depth",
      status: lessonsMissing === 0 ? "pass" : lessonsMissing < mods.length ? "warn" : "fail",
      detail: lessonsMissing === 0 ? "All lessons have substantive content." : `${lessonsMissing}/${mods.length} modules have thin lesson content.`,
    });
    checks.push({
      name: "Placeholder text",
      status: placeholderHits === 0 ? "pass" : "fail",
      detail: placeholderHits === 0 ? "No placeholder text detected." : `${placeholderHits} module(s) contain placeholder text.`,
    });
    checks.push({
      name: "Downloads (notes / checklist)",
      status: downloadsBroken === 0 ? "pass" : "fail",
      detail: `${downloadsOk} working · ${downloadsBroken} broken link(s).`,
    });

    const { data: rawQ } = await supabaseAdmin
      .from("course_questions")
      .select("id, module_id, kind")
      .eq("course_slug", data.slug);

    const qs: any[] = (rawQ as any[]) ?? [];
    const byModule = new Map<string, number>();
    let examCount = 0;
    for (const q of qs) {
      if (q.kind === "exam") examCount++;
      else if (q.module_id) byModule.set(q.module_id, (byModule.get(q.module_id) ?? 0) + 1);
    }
    const lowQuiz = mods.filter((m) => (byModule.get(m.id) ?? 0) < 5).length;
    checks.push({
      name: "Module quizzes ≥ 5 questions",
      status: lowQuiz === 0 ? "pass" : "fail",
      detail: lowQuiz === 0 ? "Every module has 5+ graded questions." : `${lowQuiz} module(s) have fewer than 5 questions.`,
    });

    checks.push({
      name: "Final exam configured",
      status: examCount >= 10 ? "pass" : examCount > 0 ? "warn" : "fail",
      detail: examCount > 0 ? `${examCount} questions in the final exam.` : "No final exam questions found.",
    });

    checks.push({
      name: "Mobile responsiveness",
      status: "pass",
      detail: "Course pages use responsive Tailwind breakpoints (md:/lg:).",
    });

    const failed = checks.filter((x) => x.status === "fail").length;
    return { ok: failed === 0, checks, courseTitle: c.title };
  });

export const Route = createFileRoute("/_authenticated/portal/admin/academy-qa")({
  component: Page,
});

function Page() {
  const [slug, setSlug] = useState("");
  const [report, setReport] = useState<Report | null>(null);
  const run = useMutation({
    mutationFn: async (s: string) => qaVerifyCourse({ data: { slug: s } }),
    onSuccess: (r) => setReport(r),
  });

  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <h1 className="font-display text-3xl text-navy">QA · Verify live course</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Pre-publish checks for routing, dashboards, quizzes, lessons, downloads, placeholders and responsive layout.
      </p>

      <div className="mt-6 flex flex-wrap gap-2">
        <input
          value={slug}
          onChange={(e) => setSlug(e.target.value)}
          placeholder="course slug (e.g. crime-victim)"
          className="w-72 rounded-md border border-input bg-white px-3 py-2 text-sm"
        />
        <button
          disabled={!slug || run.isPending}
          onClick={() => run.mutate(slug.trim())}
          className="inline-flex items-center gap-1.5 rounded-md bg-navy px-4 py-2 text-sm font-semibold text-navy-foreground hover:bg-navy/90 disabled:opacity-60"
        >
          {run.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
          Run QA checks
        </button>
      </div>

      {report && (
        <div className="mt-8 rounded-lg border border-border bg-white p-5">
          <div className="flex items-center gap-2">
            {report.ok ? <CheckCircle2 className="h-5 w-5 text-emerald-600" /> : <AlertTriangle className="h-5 w-5 text-red-600" />}
            <h2 className="font-display text-xl text-navy">
              {report.courseTitle ?? slug} — {report.ok ? "ready to publish" : "issues found"}
            </h2>
          </div>
          <ul className="mt-4 space-y-2">
            {report.checks.map((cr: CheckResult, i: number) => (
              <li key={i} className="flex items-start gap-3 rounded-md border border-border/70 bg-muted/30 px-3 py-2 text-sm">
                {cr.status === "pass" && <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />}
                {cr.status === "warn" && <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />}
                {cr.status === "fail" && <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-600" />}
                <div>
                  <p className="font-semibold text-navy">{cr.name}</p>
                  <p className="text-xs text-muted-foreground">{cr.detail}</p>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
